import { DEFAULT_PROVIDER_LAYERS, layerEntriesOf, picturesOf } from '../shared/array-types';
import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { compileSurfacePictures } from '../janus-engine/compile/from-base-encounter-media';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { selectThumbnailUrl } from '../thumbnail/core';

// The existing option-list cache owns both the rows and their picture jobs. Clearing a
// list drops its derived art too; entitlement/preselection copies never enter this cache.
const pictureJob = Symbol.for('fatetide.dynamic-select-option-picture.v1');
type Option = Record<string, unknown> & { [pictureJob]?: Promise<void> };

async function itemPicture(id: string, baseUrl: string): Promise<string | null> {
    // Only persisted item identities admitted by the list endpoint reach this route.
    // Its unsupported-identity object_inc fallback is never a picture source.
    const response = await fetch(baseUrl + 'options/api/authored_card_inputs_inc.php?id=' + encodeURIComponent(id));
    if (!response.ok) throw new Error('Option picture HTTP ' + response.status);
    const source: unknown = await response.json();
    const envelope = fromBaseRecord(source) && source._entity_base;
    if (!fromBaseRecord(envelope) || envelope.entity !== 'definition'
        || String(envelope.entity_id).toUpperCase() !== id.toUpperCase()
        || !fromBaseRecord(envelope.base)) throw new Error('Option picture definition identity');
    const base = envelope.base;
    if (!Array.isArray(base.pictures)) throw new Error('Option picture gallery missing');
    const own = compileSurfacePictures(base.pictures, id, 'item', (window as Window & { CDN_URL?: string }).CDN_URL);
    let url = selectThumbnailUrl({ pictures: { item: own } })?.trim() || null;
    if (!url && DEFAULT_PROVIDER_LAYERS.some(name => layerEntriesOf(base, name).length > 0)) {
        const adopted = await compileFetchedEntity(source);
        url = selectThumbnailUrl({ pictures: { item: picturesOf(adopted, 'item') } })?.trim() || null;
    }
    return url;
}

/** Resolve neutral cached rows before either Tom Select consumer copies/paints them. */
export async function hydrateDynamicOptionPictures(options: Option[], baseUrl: string): Promise<Option[]> {
    await Promise.all(options.map(option => {
        const id = readItemField(option, 'picture_item_id');
        if (typeof id !== 'string' || id.toUpperCase() !== String(option.value).toUpperCase()) return;
        return option[pictureJob] ??= itemPicture(id, baseUrl).then(url => {
            if (url) option.icon_url = url;
        }).catch(error => {
            // Art failure keeps the usable icon and saved value, and may be retried.
            delete option[pictureJob];
            ftDebugLog('tomSelect', { warn: 'option picture unavailable', id, err: String(error) });
        });
    }));
    return options;
}
